import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  Icon,
  Modal,
  Button,
  Input,
  Upload,
  Progress,
  Tooltip,
  message,
  Checkbox
} from 'antd'
import './ShotGenerationStyle.scss'

const { TextArea } = Input
const { Dragger } = Upload
const MP4_ACCEPT = '.mp4,video/mp4'

const isMp4File = file => {
  if (!file) return false
  const fileName = file.name?.toLowerCase() || ''
  const fileType = file.type?.toLowerCase() || ''
  return fileName.endsWith('.mp4') || fileType === 'video/mp4'
}

const captureVideoPoster = videoUrl =>
  new Promise(resolve => {
    if (!videoUrl) {
      resolve('')
      return
    }

    const video = document.createElement('video')
    const canvas = document.createElement('canvas')
    const captureTimes = [0.8, 1.5, 0.2]
    let captureIndex = 0
    let didFinish = false

    const cleanup = () => {
      video.pause()
      video.removeAttribute('src')
      video.load()
      if (video.parentNode) {
        video.parentNode.removeChild(video)
      }
    }

    const finish = posterUrl => {
      if (didFinish) return
      didFinish = true
      cleanup()
      resolve(posterUrl || '')
    }

    const captureFrame = () => {
      try {
        const width = video.videoWidth || 640
        const height = video.videoHeight || 360
        canvas.width = width
        canvas.height = height
        const context = canvas.getContext('2d')
        context.drawImage(video, 0, 0, width, height)

        const sample = context.getImageData(
          0,
          0,
          Math.min(width, 120),
          Math.min(height, 80)
        ).data
        let darkPixels = 0
        const totalPixels = sample.length / 4

        for (let index = 0; index < sample.length; index += 4) {
          const brightness =
            (sample[index] + sample[index + 1] + sample[index + 2]) / 3
          if (brightness < 18) darkPixels += 1
        }

        const isMostlyBlack = darkPixels / totalPixels > 0.92

        if (isMostlyBlack && captureIndex < captureTimes.length - 1) {
          captureIndex += 1
          video.currentTime = Math.min(
            captureTimes[captureIndex],
            Math.max(0.1, (video.duration || captureTimes[captureIndex]) - 0.1)
          )
          return
        }

        finish(canvas.toDataURL('image/jpeg', 0.82))
      } catch (error) {
        finish('')
      }
    }

    video.muted = true
    video.playsInline = true
    video.preload = 'auto'
    video.style.position = 'fixed'
    video.style.left = '-9999px'
    video.style.top = '-9999px'
    video.style.width = '1px'
    video.style.height = '1px'
    video.src = videoUrl

    video.onloadedmetadata = () => {
      const captureAt = Math.min(
        captureTimes[captureIndex],
        Math.max(0.1, (video.duration || captureTimes[captureIndex]) - 0.1)
      )
      try {
        video.currentTime = captureAt
      } catch (error) {
        finish('')
      }
    }

    video.onseeked = captureFrame
    video.onloadeddata = () => {
      if (!video.duration || video.currentTime > 0) {
        captureFrame()
      }
    }

    video.onerror = () => finish('')
    document.body.appendChild(video)
    video.load()
    window.setTimeout(() => finish(''), 6000)
  })

const CreateStyleModal = ({
  visible,
  onCancel,
  onCreate,
  isCreating,
  uploadProgress,
  creationProgress,
  currentAction
}) => {
  // Local state
  const [styleName, setStyleName] = useState('')
  const [styleDescription, setStyleDescription] = useState('')
  const [videoFiles, setVideoFiles] = useState([])
  const [thumbnailVideoFile, setThumbnailVideoFile] = useState(null)
  const [thumbnailImageFile, setThumbnailImageFile] = useState(null)
  const [isSystemStyle, setIsSystemStyle] = useState(false)

  // Previews
  const [videoPreviews, setVideoPreviews] = useState([])
  const [thumbnailVideoPreview, setThumbnailVideoPreview] = useState(null)
  const [thumbnailVideoPoster, setThumbnailVideoPoster] = useState('')
  const [thumbnailImagePreview, setThumbnailImagePreview] = useState(null)
  const videoPreviewsRef = useRef([])
  const thumbnailVideoPreviewRef = useRef(null)
  const thumbnailImagePreviewRef = useRef(null)

  const revokePreviewUrls = useCallback(() => {
    videoPreviewsRef.current.forEach(p => URL.revokeObjectURL(p.url))
    if (thumbnailVideoPreviewRef.current) {
      URL.revokeObjectURL(thumbnailVideoPreviewRef.current)
    }
    if (thumbnailImagePreviewRef.current) {
      URL.revokeObjectURL(thumbnailImagePreviewRef.current)
    }

    videoPreviewsRef.current = []
    thumbnailVideoPreviewRef.current = null
    thumbnailImagePreviewRef.current = null
  }, [])

  // Preview Modal State
  const [previewModalVisible, setPreviewModalVisible] = useState(false)
  const [previewVideos, setPreviewVideos] = useState([])
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0)
  const [previewVideoTitle, setPreviewVideoTitle] = useState('')

  useEffect(() => {
    videoPreviewsRef.current = videoPreviews
  }, [videoPreviews])

  useEffect(() => {
    thumbnailVideoPreviewRef.current = thumbnailVideoPreview
  }, [thumbnailVideoPreview])

  useEffect(() => {
    thumbnailImagePreviewRef.current = thumbnailImagePreview
  }, [thumbnailImagePreview])

  // Clean up object URLs on unmount
  useEffect(() => {
    return revokePreviewUrls
  }, [revokePreviewUrls])

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!visible) {
      resetForm()
    }
  }, [visible])

  const resetForm = () => {
    revokePreviewUrls()
    setStyleName('')
    setStyleDescription('')
    setVideoFiles([])
    setVideoPreviews([])
    setThumbnailVideoFile(null)
    setThumbnailVideoPreview(null)
    setThumbnailVideoPoster('')
    setThumbnailImageFile(null)
    setThumbnailImagePreview(null)
    setIsSystemStyle(false)
  }

  const handleVideoFilesChange = info => {
    setVideoFiles(prevFiles => {
      const newFiles = info.fileList
        .map(f => f.originFileObj || f)
        .filter(f => !prevFiles.find(vf => vf.uid === f.uid))

      if (newFiles.length === 0) return prevFiles

      const mp4Files = newFiles.filter(isMp4File)
      if (mp4Files.length !== newFiles.length) {
        message.warning('Only MP4 videos are allowed.')
      }

      if (mp4Files.length === 0) return prevFiles

      const totalVideos = prevFiles.length + mp4Files.length

      if (totalVideos > 7) {
        message.warning('Maximum 7 videos allowed per style.')
        const spaceLeft = 7 - prevFiles.length
        if (spaceLeft <= 0) return prevFiles
        mp4Files.splice(spaceLeft)
      }

      const newPreviews = mp4Files.map(file => ({
        uid: file.uid,
        name: file.name,
        url: URL.createObjectURL(file),
        file: file,
        posterUrl: '',
        posterStatus: 'loading',
        status: 'pending'
      }))

      setVideoPreviews(prevPreviews => [...prevPreviews, ...newPreviews])
      return [...prevFiles, ...mp4Files]
    })
  }

  const removeVideoFile = (e, uid) => {
    e.stopPropagation()
    setVideoFiles(prev => prev.filter(f => f.uid !== uid))
    setVideoPreviews(prev => {
      const previewToRemove = prev.find(p => p.uid === uid)
      if (previewToRemove) URL.revokeObjectURL(previewToRemove.url)
      return prev.filter(p => p.uid !== uid)
    })
  }

  const handleThumbnailVideoChange = info => {
    const file = info.file.originFileObj || info.file
    if (file && !isMp4File(file)) {
      message.warning('Only MP4 videos are allowed.')
      return
    }
    setThumbnailVideoFile(file)
    if (file) {
      if (thumbnailVideoPreview) URL.revokeObjectURL(thumbnailVideoPreview)
      setThumbnailVideoPreview(URL.createObjectURL(file))
    }
  }

  const removeThumbnailVideo = e => {
    e.stopPropagation()
    setThumbnailVideoFile(null)
    setThumbnailVideoPoster('')
    if (thumbnailVideoPreview) {
      URL.revokeObjectURL(thumbnailVideoPreview)
      setThumbnailVideoPreview(null)
    }
  }

  // Generate poster thumbnails for list previews
  useEffect(() => {
    let didCancel = false
    const previewsNeedingPosters = videoPreviews.filter(
      preview => preview.url && preview.posterStatus === 'loading'
    )

    previewsNeedingPosters.forEach(preview => {
      captureVideoPoster(preview.url).then(posterUrl => {
        if (didCancel) return
        setVideoPreviews(prev =>
          prev.map(item =>
            item.uid === preview.uid
              ? {
                ...item,
                posterUrl,
                posterStatus: posterUrl ? 'ready' : 'failed'
              }
              : item
          )
        )
      })
    })

    return () => {
      didCancel = true
    }
  }, [videoPreviews])

  // Generate poster for thumbnail video
  useEffect(() => {
    let didCancel = false

    setThumbnailVideoPoster('')

    if (thumbnailVideoPreview) {
      captureVideoPoster(thumbnailVideoPreview).then(posterUrl => {
        if (!didCancel) {
          setThumbnailVideoPoster(posterUrl)
        }
      })
    }

    return () => {
      didCancel = true
    }
  }, [thumbnailVideoPreview])

  const handleThumbnailImageChange = info => {
    const file = info.file.originFileObj || info.file
    setThumbnailImageFile(file)
    if (file) {
      if (thumbnailImagePreview) URL.revokeObjectURL(thumbnailImagePreview)
      setThumbnailImagePreview(URL.createObjectURL(file))
    }
  }

  const removeThumbnailImage = e => {
    e.stopPropagation()
    setThumbnailImageFile(null)
    if (thumbnailImagePreview) {
      URL.revokeObjectURL(thumbnailImagePreview)
      setThumbnailImagePreview(null)
    }
  }

  // Preview Logic
  const openVideoPreview = (videos, title) => {
    let videoList = []
    if (Array.isArray(videos)) {
      videoList = videos.map(v =>
        typeof v === 'string'
          ? { video_link: v }
          : v.video_link
            ? v
            : { video_link: v }
      )
    } else if (typeof videos === 'string') {
      videoList = [{ video_link: videos }]
    }

    setPreviewVideos(videoList)
    setCurrentPreviewIndex(0)
    setPreviewVideoTitle(title)
    setPreviewModalVisible(true)
  }

  const handleSubmit = () => {
    onCreate({
      styleName,
      styleDescription,
      videoFiles,
      thumbnailVideoFile,
      thumbnailImageFile,
      isSystemStyle
    })
  }

  // --- Render Helpers ---

  const getFileStatus = fileId => {
    const progress = uploadProgress[fileId]
    if (progress === undefined) return { status: 'pending', progress: 0 }
    if (progress >= 100) return { status: 'success', progress: 100 }
    return { status: 'uploading', progress }
  }

  const getOverallProgress = () => {
    if (currentAction === 'creating_style') {
      return creationProgress || 0
    }
    return 0
  }

  const getProgressStatus = () => {
    if (currentAction === 'generating_urls') return 'Preparing uploads...'
    if (currentAction === 'uploading') return 'Uploading files...'
    if (currentAction === 'creating_style') return 'Creating your style...'
    return 'Processing...'
  }

  const isUploading =
    currentAction === 'uploading' ||
    currentAction === 'generating_urls' ||
    currentAction === 'creating_style'

  const isSubmitDisabled =
    !styleName || videoFiles.length < 3 || !thumbnailVideoFile

  const videoUploadProps = {
    multiple: true,
    accept: MP4_ACCEPT,
    beforeUpload: () => false,
    onChange: handleVideoFilesChange,
    showUploadList: false,
    fileList: []
  }

  const thumbnailVideoProps = {
    accept: MP4_ACCEPT,
    beforeUpload: () => false,
    onChange: handleThumbnailVideoChange,
    showUploadList: false
  }

  const thumbnailImageProps = {
    accept: 'image/jpeg,image/png,image/webp',
    beforeUpload: () => false,
    onChange: handleThumbnailImageChange,
    showUploadList: false
  }

  const user = useSelector(state => state.user)
  const isSuperAdmin = user?.info?.is_super_admin || user?.info?.isSuperAdmin

  return (
    <>
      <Modal
        visible={visible}
        onCancel={onCancel}
        footer={null}
        centered
        width={640}
        className="create-style-modal-v2"
        title={null}
        destroyOnClose
        maskClosable={!isCreating}
        closable={!isCreating}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-icon">
            <Icon type="experiment" />
          </div>
          <div className="header-text">
            <h2>Create New Style</h2>
            <p>Customize your video style</p>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Section 1: Style Identity */}
          <div className="form-section">
            <div className="section-label">
              <span className="step-number">1</span>
              <span>Name Your Style</span>
            </div>
            <Input
              placeholder="e.g., Cinematic Documentary"
              value={styleName}
              onChange={e => setStyleName(e.target.value)}
              disabled={isCreating}
              size="large"
              className="style-name-input"
            />
            <TextArea
              placeholder="Brief description (optional)"
              value={styleDescription}
              onChange={e => setStyleDescription(e.target.value)}
              rows={2}
              disabled={isCreating}
              className="style-desc-input"
            />
            {isSuperAdmin && (
              <div
                className={`system-style-toggle-card ${isSystemStyle ? 'active' : ''
                  }`}
                onClick={() => !isCreating && setIsSystemStyle(!isSystemStyle)}
              >
                <div className="card-info">
                  <div className="card-icon">
                    <Icon type="global" />
                  </div>
                  <div className="card-text">
                    <span className="title">
                      Make this a System Style
                      <Tooltip title="System styles are available to all users. This action is restricted to superadmins.">
                        <Icon
                          type="info-circle"
                          className="info-tip"
                          style={{ marginLeft: 6, fontWeight: 'normal' }}
                          onClick={e => e.stopPropagation()}
                        />
                      </Tooltip>
                    </span>
                    <span className="subtitle">
                      Available to all users across the platform
                    </span>
                  </div>
                </div>
                <div className="card-action">
                  <Checkbox checked={isSystemStyle} disabled={isCreating} />
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Reference Videos */}
          <div className="form-section">
            <div className="section-label">
              <span className="step-number">2</span>
              <span>
                Editing Styles
                <Tooltip title="AI analyzes the editing style from these videos">
                  <Icon type="info-circle" className="info-tip" />
                </Tooltip>
              </span>
            </div>

            <p className="section-hint" style={{ marginBottom: 12 }}>
              Recommended: 5 videos (Min: 3, Max: 7). These define the pacing
              and camera movement. Format: MP4 only.
            </p>

            <Dragger
              {...videoUploadProps}
              disabled={isCreating || videoFiles.length >= 7}
              className="training-video-dragger"
            >
              <div className="dragger-content">
                <Icon type="cloud-upload" />
                <div className="dragger-text">
                  <span className="primary">Drop videos here</span>
                  <span className="secondary">
                    or click to browse (MP4 only)
                  </span>
                </div>
              </div>
            </Dragger>

            {/* Video Previews List */}
            {videoPreviews.length > 0 && (
              <div className="video-upload-list">
                {videoPreviews.map((preview, index) => {
                  const fileStatus = getFileStatus(preview.uid)
                  return (
                    <div
                      key={preview.uid}
                      className={`video-upload-item ${fileStatus.status}`}
                    >
                      <div
                        className="video-thumb"
                        onClick={() =>
                          openVideoPreview(preview.url, preview.name)
                        }
                      >
                        {preview.posterUrl ? (
                          <img src={preview.posterUrl} alt="" />
                        ) : (
                          <div className="video-thumb-placeholder">
                            {preview.posterStatus === 'loading' ? (
                              <Icon type="loading" spin />
                            ) : (
                              <Icon type="video-camera" />
                            )}
                          </div>
                        )}
                        {preview.posterUrl ? (
                          <div className="play-icon">
                            <Icon type="play-circle" />
                          </div>
                        ) : null}
                      </div>
                      <div className="video-info">
                        <div className="video-name">
                          Style Reference {index + 1}
                        </div>
                        <div className="video-filename">{preview.name}</div>
                        {isUploading && (
                          <div className="video-progress">
                            <Progress
                              percent={fileStatus.progress}
                              size="small"
                              showInfo={false}
                              status={
                                fileStatus.status === 'success'
                                  ? 'success'
                                  : 'active'
                              }
                              strokeColor={
                                fileStatus.status === 'success'
                                  ? '#10b981'
                                  : '#5057d5'
                              }
                            />
                          </div>
                        )}
                      </div>
                      <div className="video-status">
                        {fileStatus.status === 'success' ? (
                          <span className="status-success">
                            <Icon type="check-circle" theme="filled" />
                          </span>
                        ) : fileStatus.status === 'uploading' ? (
                          <span className="status-uploading">
                            {fileStatus.progress}%
                          </span>
                        ) : !isCreating ? (
                          <button
                            className="remove-btn"
                            onClick={e => removeVideoFile(e, preview.uid)}
                          >
                            <Icon type="close" />
                          </button>
                        ) : (
                          <span className="status-pending">
                            <Icon type="clock-circle" />
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Validation Info */}
            {videoFiles.length > 0 && videoFiles.length < 3 && (
              <div
                style={{
                  marginTop: 8,
                  color: '#f59e0b',
                  fontSize: 13,
                  fontWeight: 500
                }}
              >
                <Icon type="info-circle" style={{ marginRight: 4 }} />
                Please add at least {3 - videoFiles.length} more video
                {3 - videoFiles.length > 1 ? 's' : ''} to proceed.
              </div>
            )}
          </div>

          {/* Section 3: Style Card Preview */}
          <div className="form-section">
            <div className="section-label">
              <span className="step-number">3</span>
              <span>Style Card Preview</span>
            </div>
            <p className="section-hint">
              Choose how your style appears in the gallery
            </p>

            <div className="thumbnail-row">
              {/* Thumbnail Video */}
              <div className="thumbnail-item">
                <label>
                  Preview Video <span className="required">*</span>{' '}
                  <span className="optional">(MP4 only)</span>
                </label>
                {(() => {
                  const thumbStatus = getFileStatus('thumbnail_video')
                  return (
                    <div
                      className={`thumbnail-uploader ${thumbnailVideoPreview ? 'has-content' : ''
                        }`}
                    >
                      {!thumbnailVideoPreview ? (
                        <Upload
                          {...thumbnailVideoProps}
                          disabled={isCreating}
                          className="upload-trigger"
                        >
                          <div className="upload-placeholder">
                            <Icon type="video-camera" />
                            <span>Select Video</span>
                          </div>
                        </Upload>
                      ) : (
                        <div
                          className={`thumbnail-preview video ${thumbnailVideoPoster ? 'has-poster' : ''
                            }`}
                        >
                          {thumbnailVideoPoster ? (
                            <img
                              src={thumbnailVideoPoster}
                              alt="Video preview"
                            />
                          ) : (
                            <div className="video-poster-placeholder">
                              <Icon type="loading" spin />
                              <span>Capturing preview...</span>
                            </div>
                          )}
                          {thumbnailVideoPoster ? (
                            <div
                              className="play-overlay"
                              onClick={() =>
                                openVideoPreview(
                                  thumbnailVideoPreview,
                                  'Preview Video'
                                )
                              }
                            >
                              <Icon type="play-circle" theme="filled" />
                            </div>
                          ) : null}
                          {isUploading && thumbStatus.status !== 'pending' && (
                            <div className="upload-overlay">
                              <Progress
                                type="circle"
                                percent={thumbStatus.progress}
                                width={48}
                                strokeColor={
                                  thumbStatus.status === 'success'
                                    ? '#10b981'
                                    : '#fff'
                                }
                                trailColor="rgba(255,255,255,0.3)"
                              />
                            </div>
                          )}
                          {thumbStatus.status === 'success' && (
                            <div className="success-badge">
                              <Icon type="check" />
                            </div>
                          )}
                          {!isCreating && (
                            <button
                              className="remove-btn"
                              onClick={removeThumbnailVideo}
                            >
                              <Icon type="close" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })()}
                {thumbnailVideoFile ? (
                  <div
                    className="selected-thumbnail-file"
                    title={thumbnailVideoFile.name}
                  >
                    <Icon type="paper-clip" />
                    <span>{thumbnailVideoFile.name}</span>
                  </div>
                ) : null}
              </div>

              {/* Thumbnail Image */}
              <div className="thumbnail-item">
                <label>
                  Cover Image <span className="optional">(Optional)</span>
                </label>
                {(() => {
                  const imgStatus = getFileStatus('thumbnail_image')
                  return (
                    <div
                      className={`thumbnail-uploader ${thumbnailImagePreview ? 'has-content' : ''
                        }`}
                    >
                      {!thumbnailImagePreview ? (
                        <Upload
                          {...thumbnailImageProps}
                          disabled={isCreating}
                          className="upload-trigger"
                        >
                          <div className="upload-placeholder">
                            <Icon type="picture" />
                            <span>Select Image</span>
                          </div>
                        </Upload>
                      ) : (
                        <div className="thumbnail-preview image">
                          <img src={thumbnailImagePreview} alt="Cover" />
                          {isUploading && imgStatus.status !== 'pending' && (
                            <div className="upload-overlay">
                              <Progress
                                type="circle"
                                percent={imgStatus.progress}
                                width={48}
                                strokeColor={
                                  imgStatus.status === 'success'
                                    ? '#10b981'
                                    : '#fff'
                                }
                                trailColor="rgba(255,255,255,0.3)"
                              />
                            </div>
                          )}
                          {imgStatus.status === 'success' && (
                            <div className="success-badge">
                              <Icon type="check" />
                            </div>
                          )}
                          {!isCreating && (
                            <button
                              className="remove-btn"
                              onClick={removeThumbnailImage}
                            >
                              <Icon type="close" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {currentAction === 'creating_style' && (
          <div className="progress-section">
            <div className="progress-header">
              <span className="progress-label">{getProgressStatus()}</span>
              <span className="progress-percent">{getOverallProgress()}%</span>
            </div>
            <Progress
              percent={getOverallProgress()}
              status="active"
              showInfo={false}
              strokeColor={{
                '0%': '#5057d5',
                '100%': '#7c3aed'
              }}
            />
          </div>
        )}

        {/* Modal Footer */}
        <div className="modal-footer">
          <Button
            onClick={onCancel}
            disabled={isCreating}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isCreating}
            disabled={isSubmitDisabled}
            className="submit-btn"
          >
            {isCreating ? 'Creating...' : 'Create Style'}
          </Button>
        </div>
      </Modal>

      {/* Internal Video Preview Modal */}
      <Modal
        maskClosable={false}
        visible={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={null}
        centered
        width={800}
        className="video-preview-modal"
        title={previewVideoTitle || 'Video Preview'}
        destroyOnClose
      >
        <div className="video-player-wrapper">
          {previewVideos.length > 0 && (
            <video
              key={previewVideos[currentPreviewIndex]?.video_link}
              src={previewVideos[currentPreviewIndex]?.video_link}
              controls
              autoPlay
            />
          )}
        </div>

        {/* Playlist Section */}
        {previewVideos.length > 1 && (
          <div className="video-playlist">
            <div className="playlist-header">
              <Icon type="unordered-list" />
              <span>Reference Videos</span>
              <span className="count-badge">{previewVideos.length}</span>
            </div>
            <div className="playlist-grid">
              {previewVideos.map((video, index) => (
                <div
                  key={index}
                  className={`playlist-item ${index === currentPreviewIndex ? 'active' : ''
                    }`}
                  onClick={() => setCurrentPreviewIndex(index)}
                >
                  <div className="item-thumb">
                    <video
                      src={video.video_link}
                      muted
                      onLoadedData={e => (e.target.currentTime = 1)}
                    />
                    {index === currentPreviewIndex && (
                      <div className="playing-indicator">
                        <Icon type="play-circle" theme="filled" />
                      </div>
                    )}
                  </div>
                  <div className="item-index">Video {index + 1}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

export default CreateStyleModal
